import { createContext, useContext, useEffect, useMemo, useState } from "react";
import API, { TAuthProvider } from "../api";


const TOKEN_KEY = "token";
const EXPIRY_KEY = "expiry";

function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(window.atob(base64).split("").map(function(c) {
    return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(""));

  return JSON.parse(jsonPayload);
}


type ContextProps = {
  expiry?: number;
  token?: string;
  login: (authProvider: string | undefined, query: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}
const AuthContext = createContext<ContextProps | null>(null);


type Props = {
  children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [token, setToken] = useState<string>();
  const [expiry, setExpiry] = useState<number>();

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      setToken(storedToken);
    }

    const storedExpiry = localStorage.getItem(EXPIRY_KEY);
    if (storedExpiry) {
      setExpiry(parseInt(storedExpiry));
    }
  }, []);

  const login = (authProvider: string | undefined, query: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!(authProvider && API.AUTH_PROVIDERS.includes(authProvider))) {
        reject("Unknown authentication provider.");
        return;
      }
      resolve(undefined);
    })
      .then(() => API.getAuthenticationToken(authProvider as TAuthProvider, query))
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);

        const { exp } = parseJwt(data.access_token);
        localStorage.setItem(EXPIRY_KEY, exp);
        setExpiry(exp);
      })
      .catch((error) => {
        throw new Error(`Error handling OAuth success: ${error}`);
      });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    setToken(undefined);
  };

  const isAuthenticated = !!token;

  const contextValue = {
    expiry,
    token,
    login,
    logout,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const { expiry, token, login, logout, isAuthenticated } = useContext(AuthContext) as ContextProps || {};

  return useMemo(() => {
    return {
      expiry,
      token,
      login,
      logout,
      isAuthenticated
    };
  }, [expiry, token, login, logout, isAuthenticated]);
}
